import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Plain labeled text input for the auth + signup forms. Shadcn-compatible
 * styling so when we layer in the full shadcn primitives later, swapping is a
 * one-line per-call rename.
 */
type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string;
  trailing?: ReactNode;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, trailing, className, id, ...rest },
  ref,
) {
  const inputId = id ?? `f-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <label htmlFor={inputId} className="block space-y-1.5">
      <span className="block text-sm font-medium text-foreground">{label}</span>
      <div className="relative">
        <input
          {...rest}
          id={inputId}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined}
          className={cn(
            'block w-full rounded-lg border border-input bg-card px-3 py-2 text-sm shadow-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/30',
            error && 'border-destructive focus:border-destructive focus:ring-destructive/30',
            trailing && 'pr-10',
            className,
          )}
        />
        {trailing && (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-muted-foreground">
            {trailing}
          </span>
        )}
      </div>
      {error ? (
        <span id={`${inputId}-err`} className="block text-xs font-medium text-destructive">
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} className="block text-xs text-muted-foreground">
          {hint}
        </span>
      ) : null}
    </label>
  );
});
